import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { useTheme } from '@/contexts/ThemeContext';
import { SendEmailService } from '@/services/SendEmailService';
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from '@/components/ui/drawer';

interface ReportBugModalProps {
    isOpen: boolean;
    onClose: () => void;
    userEmail?: string;
    triggerSource?: string;
}

export const ReportBugModal = ({ isOpen, onClose, userEmail, triggerSource = 'Settings' }: ReportBugModalProps) => {
    const [reportText, setReportText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { theme } = useTheme();
    const isDarkMode = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleSubmit = async () => {
        if (!reportText.trim()) {
            return;
        }

        setIsSubmitting(true);
        try {
            await SendEmailService.send({
                type: 'bug_report',
                userEmail,
                data: {
                    description: reportText,
                    triggerSource
                },
                clientInfo: {
                    userAgent: navigator.userAgent,
                    platform: (navigator as any).platform || 'unknown',
                    language: navigator.language,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    referrer: document.referrer || 'direct',
                }
            });

            toast({ title: "Report Sent", description: "Thank you for your report." });
            setReportText(''); // Clear input
            onClose();
        } catch (error: any) {
            console.error('Report error:', error);
            toast({ title: "Error", description: error.message || "Failed to send report.", variant: "destructive" });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const content = (
        <div className={`w-full ${isMobile ? '' : 'max-w-lg rounded-2xl shadow-2xl overflow-hidden'} ${isDarkMode ? 'bg-[#1e1e1e] text-gray-200' : 'bg-white text-gray-800'}`}>
            <div className="flex items-center justify-between p-4 px-6 border-b border-white/5">
                {isMobile ? (
                    <DrawerTitle className="text-lg font-semibold">What happened?</DrawerTitle>
                ) : (
                    <h2 className="text-lg font-semibold">What happened?</h2>
                )}
                {isMobile ? (
                    <DrawerDescription className="sr-only">Report a bug or issue you encountered.</DrawerDescription>
                ) : (
                    <p className="sr-only">Report a bug or issue you encountered.</p>
                )}
                <button onClick={onClose} className="hover:text-white transition-colors">
                    <X className="w-5 h-5" />
                </button>
            </div>

            <div className={`p-6 ${isMobile ? 'pb-10 px-4' : 'max-h-[80vh] overflow-y-auto custom-scrollbar'}`}>
                <div className={`rounded-xl border p-2 mb-2 ${isDarkMode ? 'bg-[#2a2a2a] border-transparent' : 'bg-gray-50 border-gray-200'}`}>
                    <Textarea
                        value={reportText}
                        onChange={(e) => setReportText(e.target.value.slice(0, 2000))}
                        placeholder="Tell us about the issue you encountered"
                        className={`min-h-[200px] border-0 bg-transparent resize-none focus-visible:ring-0 placeholder:text-gray-500 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}
                    />
                </div>
                <div className="text-right text-xs text-gray-500 mb-6">
                    {reportText.length} / 2000 characters used
                </div>

                <div className="flex justify-end">
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting || !reportText.trim()}
                        className={`rounded-full px-6 ${isDarkMode ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-black/90'}`}
                    >
                        {isSubmitting ? 'Sending...' : 'Send'}
                    </Button>
                </div>
            </div>
        </div>
    );

    if (isMobile) {
        return (
            <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
                <DrawerContent 
                showHandle={false}
                className={`mx-auto flex w-full max-w-lg flex-col rounded-t-3xl border ${isDarkMode ? 'border-[#333] bg-[#1F1F1F]/95' : 'border-border/40 bg-background/95'} pb-3 shadow-2xl backdrop-blur-lg `}
                >
                    <div className="mx-auto w-full rounded-t-3xl max-w-lg overflow-y-auto no-scrollbar">
                        {content}
                    </div>
                </DrawerContent>
            </Drawer>
        );
    }

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            {content}
        </div>
    );
};
